import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { childAPI, parentAPI } from '../utils/api';
import type { Child } from '../types/child';
import type { Parent, ParentChildLinkRequest, ParentCreateRequest } from '../types/parent';

const emptyForm: ParentCreateRequest = {
  name: '',
  email: '',
  phone: '',
  relationship: 'mother',
  status: 'active',
  stripe_customer_id: '',
  children: [],
};

const getSavedParentId = (response: { data?: Parent; parent?: Parent }) => response.parent?.id || response.data?.id;

const getChildClassroomName = (child: Child) => {
  if (!child.classroom) return '';
  return typeof child.classroom === 'string' ? child.classroom : child.classroom.name;
};

const normalizeRelationship = (relationship?: string): ParentChildLinkRequest['relationship'] => {
  if (relationship === 'father' || relationship === 'guardian') return relationship;
  return 'mother';
};

const Parents: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'parents' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const { canManageParents } = useRolePermissions();
  const nurseryId = currentNursery?.nursery_id || '';
  const [parents, setParents] = useState<Parent[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [form, setForm] = useState<ParentCreateRequest>(emptyForm);
  const [editing, setEditing] = useState<Parent | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [portalUsername, setPortalUsername] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [childSearchTerm, setChildSearchTerm] = useState('');

  const selectedChildLinks = form.children || [];
  const selectedChildIds = selectedChildLinks.map((child) => child.child_id);
  const filteredChildren = children.filter((child) => {
    const search = childSearchTerm.trim().toLowerCase();
    if (!search) return true;
    return [child.name, child.child_id, getChildClassroomName(child)].some((value) => String(value || '').toLowerCase().includes(search));
  });

  const extractApiError = (err: any, fallback: string) => {
    if (err?.response?.data?.errors) {
      const errors = err.response.data.errors;
      return Object.keys(errors).map((key) => {
        const value = errors[key];
        return `${key}: ${Array.isArray(value) ? value.join(', ') : value}`;
      }).join(' | ');
    }
    return err?.response?.data?.message || err?.message || fallback;
  };

  const loadData = useCallback(async () => {
    if (!nurseryId) return;
    setLoading(true);
    setError(null);
    try {
      const [parentResponse, childResponse] = await Promise.all([
        parentAPI.list(nurseryId),
        childAPI.getChildren(nurseryId, true),
      ]);
      setParents(parentResponse.parents || parentResponse.data || []);
      setChildren(childResponse.children || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [nurseryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalError(null);
    setModalSuccess(null);
    setPortalUsername('');
    setPortalPassword('');
    setChildSearchTerm('');
    setShowModal(true);
  };

  const openEdit = (parent: Parent) => {
    setEditing(parent);
    setForm({
      name: parent.name,
      email: parent.email,
      phone: parent.phone || '',
      relationship: normalizeRelationship(parent.relationship),
      status: parent.status === 'inactive' ? 'inactive' : 'active',
      stripe_customer_id: parent.stripe_customer_id || '',
      children: parent.children?.map((child) => ({
        child_id: child.id,
        relationship: normalizeRelationship(parent.relationship),
        is_primary: Boolean(child.pivot?.is_primary),
        pickup_authorized: Boolean(child.pivot?.pickup_authorized),
      })) || [],
    });
    setModalError(null);
    setModalSuccess(null);
    setPortalUsername(parent.username || '');
    setPortalPassword('');
    setChildSearchTerm('');
    setShowModal(true);
  };

  const toggleChild = (childId: number) => {
    const existing = selectedChildLinks.find((child) => child.child_id === childId);
    if (existing) {
      setForm({ ...form, children: selectedChildLinks.filter((child) => child.child_id !== childId) });
      return;
    }

    setForm({
      ...form,
        children: [
        ...selectedChildLinks,
        {
          child_id: childId,
          relationship: normalizeRelationship(form.relationship),
          is_primary: selectedChildLinks.length === 0,
          pickup_authorized: true,
        },
      ],
    });
  };

  const updateChildLink = (childId: number, changes: Partial<ParentChildLinkRequest>) => {
    setForm({
      ...form,
      children: selectedChildLinks.map((child) => (
        child.child_id === childId ? { ...child, ...changes } : child
      )),
    });
  };

  const handleGrantPortalAccess = async () => {
    if (!nurseryId || !editing || !portalUsername || !portalPassword) return;
    setPortalLoading(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      await parentAPI.grantPortalAccess(nurseryId, editing.id, { username: portalUsername, password: portalPassword });
      setEditing({ ...editing, username: portalUsername, has_portal_access: true, must_change_password: true });
      setParents((items) => items.map((parent) => (
        parent.id === editing.id
          ? { ...parent, username: portalUsername, has_portal_access: true, must_change_password: true }
          : parent
      )));
      setPortalPassword('');
      setModalSuccess(t('portalAccessGranted'));
      await loadData();
    } catch (err: any) {
      setModalError(extractApiError(err, t('grantAccessFailed')));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleResetPortalPassword = async () => {
    if (!nurseryId || !editing || !portalPassword) return;
    setPortalLoading(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      await parentAPI.resetPortalPassword(nurseryId, editing.id, { password: portalPassword });
      setEditing({ ...editing, must_change_password: true });
      setParents((items) => items.map((parent) => (
        parent.id === editing.id ? { ...parent, must_change_password: true } : parent
      )));
      setPortalPassword('');
      setModalSuccess(t('portalPasswordReset'));
      await loadData();
    } catch (err: any) {
      setModalError(extractApiError(err, t('resetPasswordFailed')));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleRevokePortalAccess = async () => {
    if (!nurseryId || !editing) return;
    setPortalLoading(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      await parentAPI.revokePortalAccess(nurseryId, editing.id);
      setEditing({ ...editing, username: null, has_portal_access: false, must_change_password: false });
      setParents((items) => items.map((parent) => (
        parent.id === editing.id
          ? { ...parent, username: null, has_portal_access: false, must_change_password: false }
          : parent
      )));
      setShowRevokeConfirm(false);
      setPortalPassword('');
      setModalSuccess(t('portalAccessRevoked'));
      await loadData();
    } catch (err: any) {
      setModalError(extractApiError(err, t('revokeAccessFailed')));
    } finally {
      setPortalLoading(false);
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!nurseryId) return;
    setSaving(true);
    setModalError(null);
    setModalSuccess(null);
    try {
      const parentPayload: ParentCreateRequest = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        relationship: normalizeRelationship(form.relationship),
        status: form.status === 'inactive' ? 'inactive' : 'active',
        stripe_customer_id: form.stripe_customer_id || null,
      };
      const linkedChildren = selectedChildLinks.map((child) => ({
        ...child,
        relationship: normalizeRelationship(form.relationship),
      }));

      if (editing) {
        await parentAPI.update(nurseryId, editing.id, parentPayload);
        await parentAPI.linkChildren(nurseryId, editing.id, linkedChildren);
      } else {
        const response = await parentAPI.create(nurseryId, parentPayload);
        const parentId = getSavedParentId(response);
        if (parentId) {
          await parentAPI.linkChildren(nurseryId, parentId, linkedChildren);
        }
      }
      setShowModal(false);
      await loadData();
    } catch (err: any) {
      setModalError(extractApiError(err, t('saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  if (!canManageParents) {
    return <Layout><Container fluid className="py-4"><Alert variant="danger">{t('noAccess')}</Alert></Container></Layout>;
  }

  return (
    <Layout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div><h2 className="fw-bold text-primary mb-1">{t('title')}</h2><div className="text-muted">{currentNursery?.name || nurseryId}</div></div>
          <Button onClick={openCreate} disabled={!nurseryId}>{t('addParent')}</Button>
        </div>
        {!nurseryId && <Alert variant="warning">{t('selectNursery')}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light"><tr><th>{tc('labels.name')}</th><th>{tc('labels.email')}</th><th>{tc('labels.phone')}</th><th>{t('relationship')}</th><th>{t('children')}</th><th>{t('portal')}</th><th>{tc('labels.status')}</th><th className="text-end">{tc('labels.actions')}</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="text-center py-5"><Spinner animation="border" /></td></tr> : parents.map((parent) => (
                  <tr key={parent.id}>
                    <td>{parent.name}</td><td>{parent.email}</td><td>{parent.phone || '-'}</td>
                    <td>{parent.relationship ? t(`relationships.${parent.relationship}`, { defaultValue: parent.relationship }) : '-'}</td>
                    <td>{parent.children?.map((child) => child.name).join(', ') || '-'}</td>
                    <td>
                      <Badge bg={parent.has_portal_access ? 'success' : 'secondary'}>
                        {parent.has_portal_access ? t('granted') : t('noPortalAccess')}
                      </Badge>
                    </td>
                    <td><Badge bg={parent.status === 'inactive' ? 'secondary' : 'success'}>{parent.status === 'inactive' ? tc('status.inactive') : tc('status.active')}</Badge></td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(parent)}>{tc('buttons.edit')}</Button>
                      {parent.status != 'inactive' && (
                        <Button size="sm" variant="outline-danger" onClick={async () => { await parentAPI.deactivate(nurseryId, parent.id); await loadData(); }}>{t('deactivate')}</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && parents.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-5">{t('noParents')}</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
          <Form onSubmit={save}>
            <Modal.Header closeButton><Modal.Title>{editing ? t('editParent') : t('addParent')}</Modal.Title></Modal.Header>
            <Modal.Body>
              {modalError && <Alert variant="danger">{modalError}</Alert>}
              {modalSuccess && <Alert variant="success">{modalSuccess}</Alert>}
              <Row>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{tc('labels.name')}</Form.Label><Form.Control required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{tc('labels.email')}</Form.Label><Form.Control required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{tc('labels.phone')}</Form.Label><Form.Control value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Form.Group></Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('relationship')}</Form.Label>
                    <Form.Select required value={form.relationship || 'mother'} onChange={(e) => setForm({ ...form, relationship: e.target.value as ParentCreateRequest['relationship'] })}>
                      <option value="mother">{t('relationships.mother')}</option>
                      <option value="father">{t('relationships.father')}</option>
                      <option value="guardian">{t('relationships.guardian')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{tc('labels.status')}</Form.Label>
                    <Form.Select value={String(form.status || 'active')} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      <option value="active">{tc('status.active')}</option>
                      <option value="inactive">{tc('status.inactive')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('stripeCustomerId')}</Form.Label><Form.Control value={form.stripe_customer_id || ''} onChange={(e) => setForm({ ...form, stripe_customer_id: e.target.value })} /></Form.Group></Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">{t('linkedChildren')}</Form.Label>
                      <span className="text-muted small">{t('selectedCount', { count: selectedChildIds.length })}</span>
                    </div>
                    <Form.Control className="mb-2" placeholder={t('searchChildren')} value={childSearchTerm} onChange={(e) => setChildSearchTerm(e.target.value)} />
                    <div className="border rounded p-3 bg-light">
                      {selectedChildIds.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {children.filter((child) => selectedChildIds.includes(child.id)).map((child) => (
                            <Badge key={child.id} bg="primary" className="px-2 py-2">
                              {child.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <Row className="g-2">
                        {filteredChildren.map((child) => {
                          const link = selectedChildLinks.find((selectedChild) => selectedChild.child_id === child.id);
                          return (
                            <Col md={6} key={child.id}>
                              <div className="bg-white border rounded p-3">
                                <Form.Check
                                  type="checkbox"
                                  id={`parent-child-${child.id}`}
                                  label={`${child.name}${getChildClassroomName(child) ? ` - ${getChildClassroomName(child)}` : ''}`}
                                  checked={!!link}
                                  onChange={() => toggleChild(child.id)}
                                />
                                {link && (
                                  <div className="mt-3 pt-3 border-top">
                                    <div className="d-flex flex-wrap gap-3">
                                      <Form.Check
                                        type="checkbox"
                                        id={`parent-child-primary-${child.id}`}
                                        label={t('primary')}
                                        checked={!!link.is_primary}
                                        onChange={(e) => updateChildLink(child.id, { is_primary: e.target.checked })}
                                      />
                                      <Form.Check
                                        type="checkbox"
                                        id={`parent-child-pickup-${child.id}`}
                                        label={t('pickupAuthorized')}
                                        checked={!!link.pickup_authorized}
                                        onChange={(e) => updateChildLink(child.id, { pickup_authorized: e.target.checked })}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </Col>
                          );
                        })}
                        {filteredChildren.length === 0 && <Col><div className="text-muted small">{t('noChildren')}</div></Col>}
                      </Row>
                    </div>
                  </Form.Group>
                </Col>
                {editing && (
                  <Col md={12} className="mb-3 mt-2">
                    <div className="border rounded p-3 bg-light">
                      <h6 className="border-bottom pb-2 mb-3">{t('portalAccess')}</h6>
                      <div className="mb-3">
                        <span className="small text-muted me-2">{t('parentPortalUrl')}:</span>
                        <a href={`${window.location.origin}/parent/login`} target="_blank" rel="noopener noreferrer" className="small">
                          {`${window.location.origin}/parent/login`}
                        </a>
                      </div>
                      {!editing.has_portal_access ? (
                        <>
                          <p className="small text-muted mb-3">{t('grantAccessHint')}</p>
                          <Row>
                            <Col md={5}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small">{t('portalUsername')}</Form.Label>
                                <Form.Control type="text" size="sm" value={portalUsername} onChange={(e) => setPortalUsername(e.target.value)} placeholder={t('usernamePlaceholder')} />
                              </Form.Group>
                            </Col>
                            <Col md={5}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small">{t('temporaryPassword')}</Form.Label>
                                <Form.Control type="password" size="sm" value={portalPassword} onChange={(e) => setPortalPassword(e.target.value)} placeholder={t('passwordPlaceholder')} />
                              </Form.Group>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                              <Button type="button" variant="outline-primary" size="sm" className="w-100 mb-2" onClick={handleGrantPortalAccess} disabled={portalLoading || !portalUsername || !portalPassword}>
                                {portalLoading ? <Spinner animation="border" size="sm" /> : t('grant')}
                              </Button>
                            </Col>
                          </Row>
                        </>
                      ) : (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <span className="small text-muted me-2">{t('portalUsername')}:</span>
                              <Badge bg="success" className="me-2">{editing.username || portalUsername || editing.email}</Badge>
                              {editing.must_change_password && <Badge bg="warning" text="dark">{t('mustChangePassword')}</Badge>}
                            </div>
                            <Button type="button" variant="outline-danger" size="sm" onClick={() => setShowRevokeConfirm(true)} disabled={portalLoading}>{t('revokeAccess')}</Button>
                          </div>
                          <div className="border-top pt-3">
                            <h6>{t('resetPassword')}</h6>
                            <Row>
                              <Col md={9}>
                                <Form.Control type="password" size="sm" value={portalPassword} onChange={(e) => setPortalPassword(e.target.value)} placeholder={t('newTemporaryPassword')} />
                              </Col>
                              <Col md={3}>
                                <Button type="button" variant="outline-warning" size="sm" className="w-100" onClick={handleResetPortalPassword} disabled={portalLoading || !portalPassword}>
                                  {portalLoading ? <Spinner animation="border" size="sm" /> : t('resetPassword')}
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </Modal.Body>
            <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>{tc('buttons.cancel')}</Button><Button type="submit" disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : tc('buttons.save')}</Button></Modal.Footer>
          </Form>
        </Modal>
        <Modal show={showRevokeConfirm} onHide={() => setShowRevokeConfirm(false)} centered>
          <Modal.Header closeButton><Modal.Title>{t('revokeAccess')}</Modal.Title></Modal.Header>
          <Modal.Body>{t('confirmRevokeAccess')}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRevokeConfirm(false)}>{tc('buttons.cancel')}</Button>
            <Button variant="danger" onClick={handleRevokePortalAccess} disabled={portalLoading}>
              {portalLoading ? <Spinner animation="border" size="sm" /> : t('revokeAccess')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Parents;
